import generateUniqueID from "@core/utils/generateUniqueID";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import FetchService from "@core-ui/ApiServices/FetchService";
import ArticleViewService from "@core-ui/ContextServices/views/articleView/ArticleViewService";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import type { ProviderContextService, ProviderItemProps } from "@ext/articleProvider/models/types";
import ArticleTemplate from "@ext/templates/components/ArticleTemplate";
import { createContext, useContext, useState } from "react";

export type TemplateContextType = {
	templates: Map<string, ProviderItemProps>;
	selectedID: string;
};

export const TemplateContext = createContext<TemplateContextType>({
	templates: new Map(),
	selectedID: null,
});

class TemplateService implements ProviderContextService {
	private _setTemplates: (templates: Map<string, ProviderItemProps>) => void = () => {};
	private _setSelectedID: (selectedID: string) => void = () => {};
	private _isNext: boolean;

	Init = ({ children }: { children: JSX.Element }): JSX.Element => {
		const [templates, setTemplates] = useState<Map<string, ProviderItemProps>>(new Map());
		const [selectedID, setSelectedID] = useState<string>(null);
		const { isNext } = usePlatform();

		this._isNext = isNext;
		this._setTemplates = setTemplates;
		this._setSelectedID = setSelectedID;
		return <TemplateContext.Provider value={{ templates, selectedID }}>{children}</TemplateContext.Provider>;
	};

	get value(): TemplateContextType {
		return useContext(TemplateContext);
	}

	async fetchItems(apiUrlCreator: ApiUrlCreator) {
		const url = apiUrlCreator.getArticleListInGramaxDir("template");
		const res = await FetchService.fetch(url);

		if (!res.ok) return;
		const templates = await res.json();

		this.setItems(templates);
	}

	setItems(templates: ProviderItemProps[]) {
		this._setTemplates(new Map(templates.map((template) => [template.id, template])));
	}

	closeItem() {
		ArticleViewService.setDefaultView();
		if (!this._isNext) refreshPage();
		this._setSelectedID(null);
	}

	openItem(template: ProviderItemProps) {
		ArticleViewService.setView(() => <ArticleTemplate item={template} />);
		this._setSelectedID(template.id);
	}

	async addNewTemplate(apiUrlCreator: ApiUrlCreator) {
		const uniqueID = generateUniqueID();
		await FetchService.fetch(apiUrlCreator.createFileInGramaxDir(uniqueID, "template"));

		const res = await FetchService.fetch<ProviderItemProps[]>(apiUrlCreator.getArticleListInGramaxDir("template"));
		if (!res.ok) return;

		const newTemplates = await res.json();
		this.setItems(newTemplates);

		const addedTemplate = newTemplates.find((template) => template.id === uniqueID);
		return addedTemplate;
	}
}

export default new TemplateService();
